import React, { useEffect, useState } from "react";
import { path } from "../../../../constants/path";
import {
  createTTSContext,
  createTTSEmptyContext,
  getTTSContext,
  getTTSEmptyContext,
  removeTTSContext,
  updateTTSContext,
  updateTTSEmptyContext,
} from "../../../../apis/TTS/context.api";
import { NotifyConfirm } from "../../../../components/notify/Notify";
import { useGetPermissionAccessControl } from "../../../../context/PermissionAccessControl";
import TTSContextPopup from "../../components/Popup/TTSContextPopup";
import TTSContextTable from "../../components/Table/TTSContextTable";
import { isOpenPopup } from "../../utils";
import TTSContextControlPanel from "../../components/ControlPanel/TTSContextControlPanel";
import useControlPanel from "src/hooks/useControlPanel";
import useDebounce from "src/hooks/useDebounce";
import ControlPanelSystem from "src/components/controlPanelSystem/ControlPanelSystem";
import TTSContextPanelOptional from "../../components/ControlPanel/PanelOptional/TTSContextPanelOptional";
import Pagination from "src/components/Pagination/Pagination";
import { TTSContextType } from "../../constants";

const initContextData = {
  list: [],
  total_record: 0,
};

const titlePage = "TTS Context";

const TTSContext = () => {
  const permissionsAccessControl = useGetPermissionAccessControl();

  const {
    page,
    searchKey,
    numberPage,
    setNumberPage,
    limit,
    setLimit,
    status,
    setStatus,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    handleInputChange,
    handleChangePage,
  } = useControlPanel();

  const [contextData, setContextData] = useState(initContextData);
  const [filterContexts, setFilterContexts] = useState([]);

  const [recordForEdit, setRecordForEdit] = useState(null);
  const [openPopup, setOpenPopup] = useState(false);
  const [contextType, setContextType] = useState("success");

  const [errorsForm, setErrorsForm] = useState({});
  let search = useDebounce(searchKey, 500);

  const [loading, setLoading] = useState(true);
  const [isShowButtonInit, setIsShowButtonInit] = useState(false);
  const [isShowButtonUpdate, setIsShowButtonUpdate] = useState(false);

  const [isShowButtonInitError, setIsShowButtonInitError] = useState(false);
  const [isShowButtonUpdateError, setIsShowButtonUpdateError] = useState(false);

  useEffect(() => {
    setLoading(true);
    const query = {
      page: numberPage,
      limit,
      searchKey: search,
      contextType,
      startDate,
      endDate,
      status,
    };

    getTTSContext(query, (result) => {
      setContextData({ ...result });
      setFilterContexts([...result.list]);
      setLoading(false);
    });
  }, [contextType, endDate, limit, numberPage, search, startDate, status]);

  useEffect(() => {
    getTTSEmptyContext({ type: TTSContextType.SUCCESS_EMPTY }, (result) => {
      if (!loading) {
        if (result.status === "init") {
          setIsShowButtonInit(true);
        } else if (result.status === "update") {
          setIsShowButtonUpdate(true);
        } else {
          setIsShowButtonInit(false);
          setIsShowButtonUpdate(false);
        }
      }
    });
    getTTSEmptyContext({ type: TTSContextType.ERROR_EMPTY }, (result) => {
      if (!loading) {
        if (result.status === "init") {
          setIsShowButtonInitError(true);
        } else if (result.status === "update") {
          setIsShowButtonUpdateError(true);
        } else {
          setIsShowButtonInitError(false);
          setIsShowButtonUpdateError(false);
        }
      }
    });
  }, [loading]);

  useEffect(() => {
    setNumberPage(page);
  }, [page, setNumberPage, status, contextType]);

  const actionAfterCreateOrUpdate = (result, callback) => {
    if (result.data?.status === 422) {
      setErrorsForm({ ...result.data.data });
    } else {
      getTTSContext({ contextType }, (result) => {
        setContextData({ ...result });
        setFilterContexts([...result.list]);
      });
      callback();
      handleOpenPopup(!isOpenPopup, null);
    }
  };

  const addOrEdit = (intent, resetForm) => {
    if (!intent.id) {
      createTTSContext(intent, (result) => {
        actionAfterCreateOrUpdate(result, resetForm);
      });
    } else {
      updateTTSContext(intent.id, intent, (result) => {
        actionAfterCreateOrUpdate(result, resetForm);
      });
    }
  };

  const handleDelete = (contextDeleted) => {
    NotifyConfirm("warning", "Are you delete it ?", (result) => {
      if (result.isConfirmed) {
        const foundContextIndex = contextData.list.findIndex(
          (intent) => intent.id === contextDeleted.id
        );
        if (foundContextIndex !== -1) {
          setLoading(true);
          removeTTSContext(contextDeleted.id, (result) => {
            setLoading(false);
          });
          contextData.list.splice(foundContextIndex, 1);
          setContextData((prev) => {
            return {
              ...prev,
              list: [...contextData.list],
              total_record: prev.total_record - 1,
            };
          });
          setFilterContexts([...contextData.list]);
        }
      }
    });
  };

  const handleOpenPopup = (isOpen, item) => {
    setOpenPopup(isOpen);
    setRecordForEdit(item);
    setErrorsForm({});
  };

  const initEmptyContext = (type) => {
    setLoading(true);
    createTTSEmptyContext({ type }, (result) => {
      actionAfterCreateOrUpdate(result, () => {
        setLoading(false);
      });
    });
  };

  const updateEmptyContext = (type) => {
    setLoading(true);
    updateTTSEmptyContext({ type }, (result) => {
      actionAfterCreateOrUpdate(result, () => {
        setLoading(false);
      });
    });
  };

  return (
    <div>
      <div className="page-header">
        <h2>{titlePage}</h2>
      </div>

      <TTSContextPopup
        recordForEdit={recordForEdit}
        openPopup={openPopup}
        setOpenPopup={setOpenPopup}
        addOrEdit={addOrEdit}
        errorsForm={errorsForm}
        contextType={contextType}
      />

      <TTSContextControlPanel
        permissionsAccessControl={permissionsAccessControl}
        handleOpenPopup={handleOpenPopup}
        isShowButtonInit={isShowButtonInit}
        initEmptyContext={initEmptyContext}
        isShowButtonUpdate={isShowButtonUpdate}
        updateEmptyContext={updateEmptyContext}
        isShowButtonInitError={isShowButtonInitError}
        initEmptyContextError={initEmptyContext}
        isShowButtonUpdateError={isShowButtonUpdateError}
        updateEmptyContextError={updateEmptyContext}
      >
        <ControlPanelSystem
          searchKey={searchKey}
          handleInputChange={(e) => handleInputChange(e)}
          limit={limit}
          setLimit={setLimit}
          setNumberPage={setNumberPage}
          status={status}
          setStatus={setStatus}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
        />
        <TTSContextPanelOptional
          contextType={contextType}
          setContextType={setContextType}
        />
      </TTSContextControlPanel>

      <TTSContextTable
        permissionsAccessControl={permissionsAccessControl}
        data={filterContexts}
        handleOpenPopup={handleOpenPopup}
        handleDelete={handleDelete}
        status={status}
      />

      <Pagination
        pathname={path.ttsContext}
        page={numberPage}
        handleChangePage={(page) => handleChangePage(page)}
        limit={limit}
        pageSize={contextData.total_record}
      />
    </div>
  );
};

export default TTSContext;
